import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { message } from 'antd';
import { CloseSVG } from 'assets/img';
import { DropDownView, InputView, TextView } from 'components';
import useChannelDetails from 'hooks/useChannelDetails';
import { RootState } from 'redux/store/store';
import { contentManagement } from 'services/api/endPoints';
import { HTTPClient } from 'services/api/http-client';

import { YbCoreButton } from '@yubi/yb-core-button-v3';
import YbCoreDrawer from '@yubi/yb-core-drawer';
import { useStyles } from '@yubi/yb-style-provider';

interface TestTemplateProps {
  open: boolean;
  onClose: React.Dispatch<React.SetStateAction<boolean>>;
  contentData?: string[];
  variableData?: { language: string; variableMapping: string[] }[];
  id?: number;
}
const TestTemplate = ({
  open,
  onClose,
  contentData,
  variableData,
  id,
}: TestTemplateProps) => {
  const { styleConnector } = useStyles();
  const { channel: routeChannel } = useChannelDetails();
  const lenderId = useSelector(
    (state: RootState) => state.userConfiguration.lenderId
  );
  const lenderName = useSelector(
    (state: RootState) => state.userConfiguration.lenderName
  );
  const variableConfig = useSelector(
    (state: RootState) => state.content.config.data.Var
  );
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const [variables, setVariables] = useState<{ [key: string]: string }>({});
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);

  const extractVariables = () => {
    const selectedLanguageData = variableData?.find(
      (item) => item.language === selectedLanguage
    );
    return selectedLanguageData ? selectedLanguageData.variableMapping : [];
  };

  useEffect(() => {
    const requiredVariables = extractVariables();
    const allVariablesFilled = requiredVariables.every(
      (variable) => variables[variable]
    );
    setIsButtonEnabled(allVariablesFilled && !!selectedLanguage);
  }, [variables, selectedLanguage]);

  useEffect(() => {
    if (!open) {
      setSelectedLanguage('');
      setVariables({});
      setIsButtonEnabled(false);
    }
  }, [open]);

  const handleInputChange = (key: string, value: string) => {
    setVariables((prev) => ({ ...prev, [key]: value }));
  };

  const contentName = useSelector(
    (state: RootState) => state.content.content.name
  );

  const dropdownOptions =
    contentData?.map((language) => ({
      label: language,
      value: language,
    })) ?? [];

  const handleTestButtonClick = async () => {
    try {
      const response = await HTTPClient.PostRequest(
        contentManagement.POST_TEST_TEMPLATE,
        {
          lenderId,
          lenderName,
          contentId: id,
          templateName: contentName,
          channel: routeChannel,
          language: selectedLanguage,
          variables,
        }
      );
      message.success('Success! Testing successfully started.');
      onClose(false);
    } catch (error) {
      message.error('Something went wrong, please try again later');
    }
  };

  const extractVariableName = (mapping: string) => {
    const fieldName = mapping.replace(/[{}@]/g, '');
    const varEntry = variableConfig.find(
      (item) => item.fieldName === fieldName
    );
    if (varEntry) {
      return varEntry.displayName;
    } else {
      return '';
    }
  };

  return (
    <YbCoreDrawer
      closeIcon={true}
      onRequestClose={() => onClose(false)}
      open={open}
      customHeaderContent={
        <div className="rounded-tl-lg border-b border-[#D0D5DD] w-[443px] h-12 flex-shrink-0 justify-end ">
          <div className="flex flex-row h-12 pl-[160px] justify-between items-center">
            <TextView
              color="var(--text-sub-header-sh-2-n-700, var(--text-sub-header-sh-2-n-700, #344054))"
              weight="700"
              size={16}
              fontStyle="normal"
              lineHeight={24}
              text={'Test your template'}
            />
            <button onClick={() => onClose(false)}>
              <img className="pr-4" src={CloseSVG} alt=""></img>
            </button>
          </div>
        </div>
      }
      transparent
      styleConnector={styleConnector}
      customFooterContent={
        <div className="inline-flex w-full p-4 gap-4 justify-end border-t border-[#D0D5DD]">
          <div className="w-fit">
            <YbCoreButton
              label={
                <TextView
                  color="#FFF"
                  weight="500"
                  size={14}
                  fontStyle="normal"
                  lineHeight={20}
                  text={'Test'}
                />
              }
              size="m"
              loading={false}
              onClick={handleTestButtonClick}
              disabled={!isButtonEnabled}
              styleConnector={styleConnector}
              variant={'primary'}
            />
          </div>
        </div>
      }
    >
      <div className="flex flex-col pl-4 gap-4">
        <div>
          <TextView
            color="#000"
            weight="700"
            size={14}
            fontStyle="normal"
            lineHeight={20}
            text={'Select language and give variable data input for testing.'}
          />
        </div>
        <div className="z-[88]">
          <DropDownView
            dropdownData={dropdownOptions}
            handleClick={(data) => setSelectedLanguage(data.value)}
            value={selectedLanguage}
            title="Select language*"
          />
        </div>
        {extractVariables().map((mapping, index) => (
          <div key={index} style={{ marginBottom: '16px' }}>
            <TextView
              color="#000"
              weight="700"
              size={14}
              fontStyle="normal"
              lineHeight={20}
              text={`Enter ${extractVariableName(mapping)}`}
            />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <InputView
                disabled={false}
                label=""
                initialValue={variables[mapping] || ''}
                onChangeText={(text) =>
                  handleInputChange(mapping, String(text) || '')
                }
                placeholder={`Enter ${extractVariableName(mapping)}`}
                maxLength={40}
              />
            </div>
          </div>
        ))}
      </div>
    </YbCoreDrawer>
  );
};

export default TestTemplate;
